package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.domain.model.UNIVERSAL_UNIVERSE_ID
import fr.manyverse.app.domain.repository.SoundRepository

/**
 * In-memory repository containing hardcoded sound metadata.
 * Sounds are stored in composeResources/files/audio/{universe}/
 * This repository handles default sounds only. For user-uploaded sounds,
 * use SupabaseSoundRepository which wraps this repository.
 */
class InMemorySoundRepository : SoundRepository {
    val allTags = setOf(SoundTag.CALM, SoundTag.TENSION, SoundTag.COMBAT)

    private val sounds = listOf(
        Sound(
            id = "light_door_knock",
            name = "Door Knock",
            fileName = "common/effect_light_door_knock.mp3",
            type = SoundType.EFFECT,
            tags = setOf(SoundTag.CALM),
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "loud_door_knock",
            name = "Loud door knock",
            fileName = "common/effect_loud_door_knock.mp3",
            type = SoundType.EFFECT,
            tags = setOf(SoundTag.CALM, SoundTag.TENSION),
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "footsteps",
            name = "Footsteps",
            fileName = "common/effect_footsteps.mp3",
            type = SoundType.AMBIENT,
            tags = setOf(SoundTag.CALM, SoundTag.TENSION),
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "crowd",
            name = "Crowd",
            fileName = "common/ambiant_crowd.mp3",
            type = SoundType.AMBIENT,
            tags = setOf(SoundTag.CALM),
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "rain",
            name = "Rain",
            fileName = "common/ambiant_rain.mp3",
            type = SoundType.AMBIENT,
            tags = allTags,
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "loud_wind",
            name = "Strong wind",
            fileName = "common/ambiant_loud_wind.mp3",
            type = SoundType.AMBIENT,
            tags = allTags,
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "thunder",
            name = "Thunder",
            fileName = "common/ambiant_thunder.mp3",
            type = SoundType.AMBIENT,
            tags = allTags,
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "fireplace",
            name = "Fireplace",
            fileName = "common/ambiant_fireplace.mp3",
            type = SoundType.AMBIENT,
            tags = allTags,
            universeId = UNIVERSAL_UNIVERSE_ID
        ),
        Sound(
            id = "night",
            name = "Night",
            fileName = "common/ambiant_night.mp3",
            type = SoundType.AMBIENT,
            tags = allTags,
            universeId = UNIVERSAL_UNIVERSE_ID
        )
    )

    override fun getAllSounds(): List<Sound> = sounds

    override fun getSoundsByTags(tags: Set<SoundTag>): List<Sound> {
        if (tags.isEmpty()) return sounds
        return sounds.filter { sound ->
            sound.tags.any { it in tags }
        }
    }

    override fun getSoundById(id: String): Sound? {
        return sounds.find { it.id == id }
    }

    override fun getSoundsByUniverse(universeId: String): List<Sound> {
        return sounds.filter {
            it.universeId == universeId || it.universeId == UNIVERSAL_UNIVERSE_ID
        }
    }

    // The following methods are not supported by InMemorySoundRepository.
    // Use SupabaseSoundRepository which wraps this repository for full functionality.

    override suspend fun getSoundsByUniverseAndUser(
        universeId: String,
        userId: String
    ): Result<List<Sound>> {
        // Returns only default sounds for this universe
        return Result.success(getSoundsByUniverse(universeId))
    }

    override suspend fun uploadSound(
        userId: String,
        universeId: String,
        name: String,
        type: SoundType,
        tags: Set<SoundTag>,
        audioData: AudioData
    ): Result<Sound> {
        return Result.failure(UnsupportedOperationException("InMemorySoundRepository does not support uploading sounds"))
    }

    override suspend fun deleteSound(soundId: String, userId: String): Result<Unit> {
        return Result.failure(UnsupportedOperationException("InMemorySoundRepository does not support deleting sounds"))
    }

    override suspend fun getAudioBytes(sound: Sound): Result<ByteArray> {
        return Result.failure(UnsupportedOperationException("InMemorySoundRepository does not support getting audio bytes"))
    }
}
